import router from '@/router';
import { createNamespacedHelpers } from 'vuex';
import apolloClient from '@/vue-apollo';
import availableRoomsInfo from '@/queries/availableRoomsInfo';
import fetchAboutPlace from '@/queries/fetchAboutPlace';
import fetchHotel from '@/queries/fetchHotel';
import checkoutCart from '@/mutations/checkoutCart';
import _get from 'lodash/get';
import _union from 'lodash/union';
import {
  getDecryptedValue,
  storeEncryptedValue,
  decryptAttribute
} from '@/utils/crypto';
import { getLengthKm } from '@/utils/mapUtils';
import { capitalizeDestination } from '@/utils/destination';
import { getRoomsBooking } from '@/utils/roomsAvailable';
import { connectors } from '@/utils/connectors';
import { logMixPanelEvent, mixPanelEvent } from '../../utils/mixpanel';
import tmcTaxInclusive from '@/queries/tmcTaxInclusive';

export const namespacedHelper = createNamespacedHelpers('details');

const policyIcons = {
  POLICY_HOTEL_GROUPS: 'mdi-account-multiple',
  POLICY_HOTEL_INTERNET: 'mdi-wifi',
  POLICY_HOTEL_PARKING: 'mdi-car',
  POLICY_HOTEL_PETS: 'mdi-paw',
  POLICY_CHILDREN: 'mdi-human-child'
};

const state = {
  selectedTabKey: 'overview',
  loading: false,
  currency: null,
  city: null,
  hotelDetails: null,
  hotelRoomsAvailable: [],
  placeInfo: null,
  checkoutInfo: null,
  roomsLoading: false,
  sendingRequest: false,
  isTmcTaxInclusive: false
};

const getters = {
  getCheckoutInfo: state => {
    return getDecryptedValue(
      state.checkoutInfo || window.localStorage.getItem('CHECKOUT_INFO')
    );
  },
  getCurrency(state) {
    return state.currency;
  },
  getSelectedTabKey(state) {
    return state.selectedTabKey;
  },
  getHotelDetails(state) {
    return state.hotelDetails;
  },
  getHotelDetailsInfo(state) {
    if (!state.hotelDetails) {
      return null;
    }

    let decryptedHotelInfo = decryptAttribute(
      state.hotelDetails.jsonDetailedInfo
    );
    return JSON.parse(decryptedHotelInfo);
  },
  getHotelRooms(state) {
    if (!state.hotelDetails) {
      return null;
    }

    let decryptedHotelInfo = decryptAttribute(
      state.hotelDetails.jsonDetailedInfo
    );
    return JSON.parse(decryptedHotelInfo).room_data;
  },
  getHotelRoomsAvailable(state) {
    return state.hotelRoomsAvailable;
  },
  getPlaceInfo(state) {
    return state.placeInfo;
  },
  getPlaceCenter(state) {
    if (
      state.placeInfo &&
      state.placeInfo.longitude &&
      state.placeInfo.latitude
    ) {
      return [state.placeInfo.longitude, state.placeInfo.latitude];
    }

    return false;
  },
  getPlaceFacilities(state, getters) {
    if (!state.placeInfo) {
      return null;
    }

    const lengthKm = getLengthKm(
      getters.getPlaceCenter,
      getters.getHotelMapCenter
    );

    return [
      {
        icon: 'mdi-map-marker',
        title: `<strong>${lengthKm}</strong> kilometers from hotel to <strong>${getters.getCity}</strong> city center`
      }
    ];
  },
  getName(state) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.name;
  },
  getLabel(state) {
    if (!state.hotelDetails.label) {
      return '';
    }
    return state.hotelDetails.label;
  },
  getCity(state) {
    return state.city ? capitalizeDestination(state.city) : null;
  },
  getRating(state, getters) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.rating || getters.getHotelDetailsInfo.class;
  },
  getChainCode(state) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.chainCode;
  },
  getReviewTitle(state) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.reviewTitle;
  },
  getReviewCount(state) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.reviewCount;
  },
  getReviewScore(state) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.reviewScore;
  },
  getDescription(state) {
    if (state.hotelDetails && state.hotelDetails.shortDescription) {
      return state.hotelDetails.shortDescription;
    }

    return '';
  },
  getHotelAddress(state /*, getters*/) {
    if (!state.hotelDetails) {
      return '';
    }
    return state.hotelDetails.address;
  },
  getHotelMapCenter(state /*, getters*/) {
    if (!state.hotelDetails) {
      return '';
    }
    //const location = getters.hotelDetails.location;
    return [state.hotelDetails.lng, state.hotelDetails.lat];
  },
  getPlaceLoadOptions(state, getters) {
    return {
      place: getters.getCity,
      lat: state.hotelDetails.lat,
      lng: state.hotelDetails.lng
    };
  },
  getMasonry(state /*, getters*/) {
    const images = JSON.parse(JSON.stringify(state.hotelDetails.pictures));
    let hotelImages = [];
    let roomImages = [];

    if (state.hotelDetails.source === connectors.zentrumhub) {
      const standardSizedImages = images.filter(
        res =>
          res['size'] === 'Standard' ||
          res['size'] === 'Unknown' ||
          res['size'] === 'Xxl' ||
          res['size'] === 'Xl'
      );
      hotelImages = standardSizedImages.map(item => item.url);
    } else {
      hotelImages = images.map(item => item.url_original);
    }

    let imagesLength = hotelImages.length;

    if (imagesLength > 4) {
      return hotelImages;
    }

    // const roomImages = getters.getHotelRooms
    //   .filter(item => {
    //     let images = imagesLength;
    //     if (item.room_photos) {
    //       imagesLength += item.room_photos.length;
    //     }
    //     return images < 5;
    //   })
    //   .map(item => {
    //     return item.room_photos.map(item => item.url_original);
    //   })
    //   .flat();

    return _union(hotelImages, roomImages);
  },
  getGallery(state) {
    const images = JSON.parse(JSON.stringify(state.hotelDetails.pictures));
    const XLSizedImages = images.filter(
      res => res['size'] === 'Xl' || res['size'] === 'Xxl'
    );
    const LSizedImages = images.filter(
      res => res['size'] === 'L' || res['size'] === 'l'
    );

    if (XLSizedImages.length > 0) {
      return XLSizedImages.map(item => ({
        src: item.url,
        thumb: item.url,
        caption: ''
      }));
    } else {
      return LSizedImages.map(item => ({
        src: item.url,
        thumb: item.url,
        caption: ''
      }));
    }
  },
  getHotelMixPanelOptions(state /*, getters*/) {
    if (!state.hotelDetails) {
      return '';
    }

    return {
      hotelName: state.hotelDetails.name,
      hotelStarRating: state.hotelDetails.rating,
      hotelCoordinatesLonLat: [
        state.hotelDetails.lng,
        state.hotelDetails.lat
      ].join(', '),
      hotelChainId: state.hotelDetails.id,
      hotelId: state.hotelDetails.extId,
      hotelCity: state.hotelDetails.city,
      hotelSource: state.hotelDetails.source
    };
  },
  isRoomExist: (state, getters) => chosenRoom => {
    if (getters.getHotelRoomsAvailable.length === 0 || !chosenRoom) {
      return false;
    }

    return (
      getters.getHotelRoomsAvailable.filter(item => {
        return !!item.rooms.find(
          room =>
            (room.id || room.block_id) ===
            (chosenRoom.selectedType.id || chosenRoom.selectedType.block_id)
        );
      }).length > 0
    );
  },
  getRoomsLoading: state => state.roomsLoading,
  getSendingRequest: state => state.sendingRequest,
  getThingsToKnow(state, getters) {
    if (
      !state.hotelDetails ||
      state.hotelDetails.source === connectors.zentrumhub ||
      state.hotelDetails.source === connectors.expedia
    ) {
      return '';
    }

    const checkOptions =
      getters.getHotelDetailsInfo.hotel_data.checkin_checkout_times;

    const policies = getters.getHotelDetailsInfo.hotel_data.hotel_policies.map(
      item => ({
        icon: policyIcons[item.type],
        title: item.content
      })
    );

    return [
      {
        icon: 'mdi-clock-outline',
        title: `Check in: From <strong>${checkOptions.checkin_from}</strong>`
      },
      {
        icon: 'mdi-clock-outline',
        title: `Check out: By <strong>${checkOptions.checkout_to}</strong>`
      },
      ...policies
    ];
  },
  getTmcTaxInclusionInfo(state) {
    return state.isTmcTaxInclusive;
  }
};

const mutations = {
  updateCity(state, payload) {
    state.city = payload;
  },
  updateCurrency(state, payload) {
    state.currency = payload;
  },
  updateSelectedTabKey(state, payload) {
    state.selectedTabKey = payload;
  },
  setHotelDetails(state, payload) {
    state.hotelDetails = payload;
  },
  updateHotelRoomsAvailable(state, payload) {
    state.hotelRoomsAvailable = payload;
  },
  setPlaceInfo(state, payload) {
    state.placeInfo = payload;
  },
  setLoading(state, value) {
    state.loading = value;
  },
  updateCheckoutInfo(state, payload) {
    state.checkoutInfo = storeEncryptedValue('CHECKOUT_INFO', payload);
  },
  updateSendingRequest(state, payload) {
    state.sendingRequest = payload;
  },
  setRoomsLoading(state, payload) {
    state.roomsLoading = payload;
  },
  setTmcTaxInclusive(state, payload) {
    state.isTmcTaxInclusive = payload;
  }
};

const actions = {
  setCity({ commit }, value) {
    commit('updateCity', value);
  },
  setSelectedKey({ commit, rootGetters, getters }, selectedTabKey) {
    commit('updateSelectedTabKey', selectedTabKey);
    let event =
      mixPanelEvent.hotelEvents.hotelDetailEvents[
        `room${selectedTabKey[0].toUpperCase() + selectedTabKey.slice(1)}`
      ];
    logMixPanelEvent(
      event,
      {
        userId: rootGetters['user/userId'],
        activeTripId: rootGetters['user/activeTripId'],
        hotel: getters.getHotelMixPanelOptions,
        distinct_id: rootGetters['user/userId']
      },
      rootGetters['user/getLocationCoordinates']
    );
  },
  loadPlaceInfo({ commit }, options) {
    fetchAboutPlace({
      apollo: apolloClient.defaultClient,
      ...options
    })
      .then(response => _get(response, 'data.fetchInfoAbout', {}))
      .then(response => {
        commit('setPlaceInfo', response);
      });
  },
  setHotelRoomsAvailable({ commit }, val) {
    commit('updateHotelRoomsAvailable', val);
  },
  async loadAvailableRooms(
    { getters, rootGetters, commit, dispatch },
    options
  ) {
    commit('setRoomsLoading', true);

    // Everything up to the first .then runs synchronously, so a throw here
    // (no hotel loaded yet) rejected the action before any .catch existed and
    // left roomsLoading stuck true — an indefinite spinner under "Rooms".
    if (!getters.getHotelDetails) {
      commit('setRoomsLoading', false);
      return false;
    }

    let request;
    try {
      request = availableRoomsInfo({
        apollo: apolloClient.defaultClient,
        ...options,
        hotelId: getters.getHotelDetails.extId,
        corporationId: rootGetters['user/corporationId'],
        accountNumber: rootGetters['user/accountNumber'],
        emailId: rootGetters['user/emailId'],
        source: options.source,
        tripId: rootGetters['user/activeTripId']
      });
    } catch (error) {
      commit('setRoomsLoading', false);
      console.warn(error);
      return false;
    }

    return request
      .then(response => _get(response, 'data.availableRoomsInfo', {}))
      .then(response => {
        commit('setRoomsLoading', false);

        if (!response || !response[0]) {
          dispatch('setHotelRoomsAvailable', []);
          return false;
        }

        let decrypted = response[0].availableRoomsInfo;
        commit('updateCurrency', response[0].currency);

        let jsonParsedResult = JSON.parse(decrypted);

        if (options.source !== connectors.booking) {
          dispatch('setHotelRoomsAvailable', jsonParsedResult);
          return jsonParsedResult;
        } else if (options.source === connectors.booking) {
          const rooms = getRoomsBooking(
            jsonParsedResult.result,
            getters.getHotelRooms
          );
          dispatch('setHotelRoomsAvailable', rooms);
          return rooms;
        }

        return false;
      })
      .catch(error => {
        commit('setRoomsLoading', false);
        console.warn(error);
      });
  },
  async loadHotelDetails({ getters, rootGetters, commit, dispatch }, options) {
    commit('setLoading', true);
    // Handle case where hotelExtId is 0
    if (options.hotelId == 0) {
      commit('setLoading', false);
      let params = router.currentRoute.value.params;
      delete params.hotelId;

      await router.push({
        name: 'destination',
        params: params,
        query: router.currentRoute.value.query
      });

      return; // Exit the method to avoid further processing
    }
    return await fetchHotel({
      apollo: apolloClient.defaultClient,
      hotelExtId: options.hotelId,
      tripId: rootGetters['user/activeTripId'],
      source: rootGetters['detailsFilter/getSource']
    })
      .then(response => _get(response, 'data.fetchHotel', {}))
      .then(response => {
        commit('setHotelDetails', response);
        commit('setLoading', false);
        dispatch('loadPlaceInfo', getters.getPlaceLoadOptions);
        if (response.hotelFacilities.length) {
          dispatch(
            'hotelDetails/setHotelFacilities',
            response.hotelFacilities,
            {
              root: true
            }
          );
        }
        return response;
      })
      .catch(error => {
        dispatch(
          'notifications/addNotification',
          {
            message:
              'The rate you requested is no longer available, so we’ve redirected you to the search results to find something new'
          },
          {
            root: true
          }
        );
        commit('setLoading', false);
        console.warn(error);
        let params = router.currentRoute.value.params;
        delete params.hotelId;

        router.push({
          name: 'destination',
          params: params,
          query: router.currentRoute.value.query
        });
      });
  },
  async reserveRoom({ getters, rootGetters, commit }) {
    commit('updateSendingRequest', true);

    // An email_id-only session has no user record. marvin-core reads the
    // cart's uid for the email display string and the Tinybird event only —
    // the traveller is looked up from tripId — so an empty uid is safe.
    const sessionUser = rootGetters['user/user'] || {};

    const checkoutCard = {
      uid: sessionUser.userId || '',
      tripId: rootGetters['user/activeTripId'],
      hotel: getters.getHotelMixPanelOptions,
      room: rootGetters['detailsFilter/getCheckoutCartRoom'] && {
        ...rootGetters['detailsFilter/getCheckoutCartRoom'],
        costAllocationReference: rootGetters['user/getReference']
      },
      details: {
        ...rootGetters['detailsFilter/getDetailsFiltersValues'],
        ...rootGetters['detailsFilter/getDetailsFilterMixPanel']
      },
      taxInclusive: getters.getTmcTaxInclusionInfo
    };
    const encoded = btoa(
      unescape(encodeURIComponent(JSON.stringify(checkoutCard)))
    );

    if (
      JSON.parse(process.env.VUE_APP_TEST_USER) &&
      process.env.VUE_APP_TEST_USER_UID === sessionUser.userId
    ) {
      // keep the cart like a real request does: the confirmation page is built from it
      commit('updateCheckoutInfo', checkoutCard);
      commit('updateSendingRequest', false);
      await router.push({
        name: 'confirmation',
        params: router.currentRoute.value.params,
        query: router.currentRoute.value.query
      });
      return true;
    }

    return checkoutCart({
      apollo: apolloClient.defaultClient,
      jsonCart: encoded,
      emailId: rootGetters['user/emailId']
    })
      .then(response => _get(response, 'data.checkoutCart', {}))
      .then(response => {
        commit('updateSendingRequest', false);
        if (!response.success) {
          return false;
        }
        commit('updateCheckoutInfo', checkoutCard);
        commit('updateSendingRequest', false);
        router.push({
          name: 'confirmation',
          params: router.currentRoute.value.params,
          query: router.currentRoute.value.query
        });
        logMixPanelEvent(
          mixPanelEvent.cartOptions.basketRequest,
          {
            userId: checkoutCard.uid,
            activeTripId: checkoutCard.tripId,
            hotel: checkoutCard.hotel,
            room: checkoutCard.room,
            details: checkoutCard.details,
            distinct_id: rootGetters['user/userId']
          },
          rootGetters['user/getLocationCoordinates']
        );
        return response;
      })
      .catch(error => {
        commit('updateSendingRequest', false);
        error;
      });
  },
  async getTmcTaxInfo({ commit }) {
    tmcTaxInclusive({
      apollo: apolloClient.defaultClient
    })
      .then(response => _get(response, 'data.tmcTaxInclusive', {}))
      .then(response => {
        commit('setTmcTaxInclusive', response.is_tax_inclusive);
      });
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
